/**
 * PipelineInsights — the band above the pipeline table.
 *
 * Four headline measures and two charts: where deals stand, and where the
 * live pipeline sits by market. Every panel carries its own method, because
 * the thing worth keeping from the dashboards this was modelled on is not any
 * particular chart — it is that nothing on the screen is unexplained.
 *
 * WHAT IS DELIBERATELY NOT HERE. A monthly in/out chart used to sit beside
 * the funnel, and a KPI counted records added in the last 90 days. Both were
 * built on `created_at`, which for a migrated database is the month the row
 * was loaded, not the month the deal was sourced — they drew the September
 * migration and called it deal flow. The matching sparkline on "Live deals"
 * had the same fault at three pixels tall, where nobody thought to question
 * it. They come back when there is a sourced-on date to read, not before.
 *
 * "Why deals die" moved to Review Pipeline, where it can be scoped by date
 * and MSA. killMatrix() is unchanged and still lives in pipelineAnalytics.
 *
 * It collapses. Somebody who opens Pipeline to find one deal should not have
 * to scroll past analytics to reach the table, so the state is remembered.
 */

import { useEffect, useMemo, useState } from "react";
import { supabase, fetchAllRows } from "../../supabase";
import * as B from "../common/brand";
import { Funnel, Explain } from "../common/Charts";
import { collapseSlips } from "../../services/stageLog";
import {
  pipelineFunnel, marketShare, pipelineKpis, IRR_BAR, deepestStageIndex,
} from "../../services/pipelineAnalytics";

const sf0 = (v) => (Number.isFinite(v) && v ? Math.round(v).toLocaleString() : "—");
const pct0 = (v) => (Number.isFinite(v) ? `${Math.round(v * 100)}%` : "—");
const bigMoney = (v) => {
  if (!Number.isFinite(v) || !v) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  return `$${Math.round(v).toLocaleString()}`;
};

function Panel({ title, sub, how, right, children, className = "" }) {
  return (
    <section className={`${B.CARD} ${className}`}>
      <header className={B.CARD_HEAD}>
        <div className="min-w-0">
          <h3 className={B.TITLE}>{title}</h3>
          {sub && <p className={`${B.SUBTITLE} mt-0.5`}>{sub}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {right}
          {how && <Explain>{how}</Explain>}
        </div>
      </header>
      {children}
    </section>
  );
}

/** A KPI that carries its method, not just its value. */
function Kpi({ label, value, sub, how, tone = "bright" }) {
  const tones = { bright: "text-[#f4f4f4]", up: "text-[#4f9d76]", down: "text-[#e93529]", warn: "text-[#c8922f]" };
  return (
    <div className="px-5 py-4 flex-1 min-w-[168px]">
      <div className="flex items-start justify-between gap-2">
        <div className={B.LABEL}>{label}</div>
        {how && <Explain label="?" >{how}</Explain>}
      </div>
      <div className="mt-1.5">
        <span className={`text-[24px] leading-none font-light tracking-tight tabular-nums ${tones[tone]}`}>{value}</span>
      </div>
      {sub && <div className="mt-2 text-[11px] text-[#8a8a8a] leading-snug">{sub}</div>}
    </div>
  );
}

export default function PipelineInsights({ deals = [] }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("pp.pipelineInsights") !== "closed"; } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("pp.pipelineInsights", open ? "open" : "closed"); } catch { /* private mode */ }
  }, [open]);

  // The deepest stage each deal ever reached, read from activity_log the same
  // way the Review funnel reads it. Without this the funnel sees a dead deal
  // at whatever it died as, so every deal underwritten before it was passed or
  // lost vanishes from the top — 147 of the reviewed 206 sit at Passed.
  // Loaded here rather than threaded through props because this panel is the
  // only thing on Pipeline that needs it, and it collapses by default.
  const [stageHistory, setStageHistory] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await fetchAllRows(() => supabase
        .from("activity_log")
        .select("entity_id, action, metadata, created_at")
        .eq("entity_type", "deal")
        .eq("action", "stage_changed")
        .order("created_at")
        .order("id"));
      if (cancelled) return;
      if (error) { console.error("stage history load failed:", error.message); return; }
      setStageHistory(deepestStageIndex(collapseSlips(data || [])));
    })();
    return () => { cancelled = true; };
  }, []);

  const funnel = useMemo(() => pipelineFunnel(deals, stageHistory), [deals, stageHistory]);
  const markets = useMemo(() => marketShare(deals), [deals]);
  const kpi = useMemo(() => pipelineKpis(deals), [deals]);

  const topShare = markets.top && Number.isFinite(markets.top.share) ? markets.top.share : null;

  return (
    <div className="mb-5 space-y-4">
      <div className={`${B.CARD} flex flex-wrap divide-x divide-[#303030]`}>
        <Kpi label="Live deals" value={String(kpi.live)}
             sub={`${sf0(kpi.liveSf)} SF across them`} how={kpi.how.live} />
        <Kpi label="Asking, live" value={bigMoney(kpi.liveValue)}
             sub={`priced on ${kpi.valueKnown} of ${kpi.live} deals`} how={kpi.how.value} />
        <Kpi label="Off-market" value={pct0(kpi.offMarketShare)}
             sub={kpi.live
               ? `${kpi.offMarket} of ${kpi.live} live deal${kpi.live === 1 ? "" : "s"} came to us off-market`
               : "nothing live to measure"}
             how={kpi.how.offMarket} />
        <Kpi label="Median levered IRR"
             value={kpi.medianIrr == null ? "—" : `${kpi.medianIrr.toFixed(1)}%`}
             tone={kpi.medianIrr == null ? "bright" : kpi.medianIrr >= IRR_BAR ? "up" : "warn"}
             sub={kpi.irrKnown
               ? `across the ${kpi.irrKnown} of ${kpi.live} that are underwritten · ${IRR_BAR}% bar`
               : "no live deal carries a return yet"}
             how={kpi.how.irr} />
        <div className="px-4 py-4 flex items-center">
          <button onClick={() => setOpen((v) => !v)}
            className="text-[11px] uppercase tracking-[0.12em] text-[#8a8a8a] hover:text-[#f4f4f4] transition whitespace-nowrap">
            {open ? "Hide charts" : "Show charts"}
          </button>
        </div>
      </div>

      {open && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <Panel title="Where deals stand" className="xl:col-span-2"
                 sub={`${funnel.live} live, ${funnel.dead} closed out — counted by the furthest stage each can be shown to have reached`}
                 how={funnel.how}>
            <div className="px-3 pt-3">
              <Funnel stages={funnel.stages} height={196} />
            </div>
            <div className="px-5 pb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {funnel.stages.map((s) => (
                <div key={s.label} className="rounded-lg border border-[#3a3a3a] bg-[#2c2c2c] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-[0.1em] text-[#8a8a8a] truncate">{s.label}</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-[15px] font-light tabular-nums text-[#f4f4f4]">{s.standing}</span>
                    <span className="text-[10.5px] text-[#8a8a8a]">standing</span>
                  </div>
                  <div className="text-[10.5px] text-[#8a8a8a] mt-0.5">
                    {sf0(s.sf)} SF reached{s.dropped ? ` · ${s.dropped} died here` : ""}
                  </div>
                </div>
              ))}
            </div>
            {funnel.unevidenced > 0 && (
              <p className="px-5 pb-4 text-[10.5px] text-[#7a7a7a] leading-relaxed">
                {funnel.unevidenced} closed deal{funnel.unevidenced === 1 ? "" : "s"} carr{funnel.unevidenced === 1 ? "ies" : "y"} no
                record of how far {funnel.unevidenced === 1 ? "it" : "they"} got, so {funnel.unevidenced === 1 ? "it counts" : "they count"} only
                at the top. Conversion below is understated by that much, not flattered.
              </p>
            )}
          </Panel>

          <Panel title="Where the pipeline sits"
                 sub={markets.empty
                   ? "no live deal carries an area"
                   : `${markets.ranked.length} market${markets.ranked.length === 1 ? "" : "s"}`
                     + (topShare != null ? ` · ${pct0(topShare)} of live SF in ${markets.top.msa}` : "")}
                 how={markets.how}>
            {markets.empty ? (
              <div className="px-5 py-8 text-[12.5px] text-[#8a8a8a] leading-relaxed">
                Nothing live carries both an MSA and a building area, so there is
                no concentration to read.
              </div>
            ) : (
              <div className="px-5 py-4">
                <div className={`${B.LABEL} mb-2`}>Live building area by market</div>
                <div className="space-y-1.5 max-h-[15rem] overflow-y-auto pr-1">
                  {markets.ranked.map((r) => {
                    const max = markets.ranked[0].sf || 1;
                    return (
                      <div key={r.msa} className="flex items-center gap-2.5">
                        <span className="w-28 shrink-0 text-[11.5px] text-[#b4b4b4] truncate" title={r.msa}>{r.msa}</span>
                        <div className="flex-1 h-2 rounded-full bg-[#2c2c2c] overflow-hidden">
                          <div className="h-full rounded-full"
                               style={{ width: `${(r.sf / max) * 100}%`, background: "rgba(233,53,41,0.5)" }} />
                        </div>
                        <span className="w-9 text-right text-[11.5px] tabular-nums text-[#f4f4f4]">{pct0(r.share)}</span>
                        <span className="w-20 text-right text-[10.5px] tabular-nums text-[#8a8a8a]">{sf0(r.sf)} SF</span>
                      </div>
                    );
                  })}
                </div>
                {(markets.overlap > 0 || markets.unassigned > 0) && (
                  <p className="mt-3 text-[10.5px] text-[#7a7a7a] leading-relaxed">
                    {markets.overlap > 0 && (
                      <>{markets.overlap} deal{markets.overlap === 1 ? " spans" : "s span"} more than one market and
                      count{markets.overlap === 1 ? "s" : ""} in full under each, so the shares sum above 100%. </>
                    )}
                    {markets.unassigned > 0 && (
                      <>{markets.unassigned} carr{markets.unassigned === 1 ? "ies" : "y"} no MSA.</>
                    )}
                  </p>
                )}
              </div>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
